import React, { useState } from 'react';
import { Info, Copy, Check } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@/stores/authStore';

export type MetricDefinition = {
  id: string;
  label: string;
  group: string;
  rulePt: string;
  sqlFragment: string;
  sqlFull: string;
};

type Props = {
  metricId: string;
  label?: string;
  definicoes?: MetricDefinition[];
  className?: string;
  stopPropagation?: boolean;
};

export const MetricDefinitionButton: React.FC<Props> = ({
  metricId,
  label,
  definicoes = [],
  className = '',
  stopPropagation = false,
}) => {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'ADMIN';
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const def = definicoes.find((d) => d.id === metricId);
  if (!def) return null;

  const modalTitle = label ?? def.label;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(def.sqlFull);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          if (stopPropagation) e.stopPropagation();
          setOpen(true);
        }}
        className={`inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground hover:text-primary hover:bg-muted transition-colors ${className}`}
        aria-label={`Ver definição: ${modalTitle}`}
        title="Ver como este indicador é calculado"
      >
        <Info className="h-3.5 w-3.5" />
      </button>

      <Modal isOpen={open} onClose={() => setOpen(false)} title={modalTitle} size="lg">
        <div className="space-y-4 text-left">
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-1">Regra</h4>
            <p className="text-sm text-muted-foreground leading-relaxed">{def.rulePt}</p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground mb-1">Condição resumida</h4>
            <pre className="text-xs bg-muted p-3 rounded-md overflow-x-auto whitespace-pre-wrap font-mono text-foreground">
              {def.sqlFragment}
            </pre>
          </div>

          {isAdmin ? (
            <div>
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-semibold text-foreground">SQL completo</h4>
                <Button type="button" variant="outline" size="sm" onClick={() => void handleCopy()}>
                  {copied ? (
                    <>
                      <Check className="h-3.5 w-3.5 mr-1" /> Copiado
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5 mr-1" /> Copiar SQL
                    </>
                  )}
                </Button>
              </div>
              <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded-md overflow-x-auto whitespace-pre-wrap font-mono max-h-64 overflow-y-auto">
                {def.sqlFull}
              </pre>
            </div>
          ) : null}
        </div>
      </Modal>
    </>
  );
};
